// Detalle de un envío de "Envíos > Lista legacy" (legacy.envios). Ficha
// operativa de solo lectura — mismo patrón de overlay que los otros modales
// de detalle del panel admin (ver DriverAdminDetailModal.tsx). Sin edición,
// sin financials. El mapa reutiliza useLeaflet (mismo hook que Choferes en
// vivo / resolución de geolocalización) — sin dependencia nueva, solo
// lectura, un marcador fijo, sin click-to-place ni geocodificación.
import React, { useEffect, useRef, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import { authFetch } from "../../lib/authFetch";
import { useLeaflet } from "../../hooks/useLeaflet";
import { useAuth } from "../../context/AuthContext";
import OverrideEditModal, { DeliveryDetail, ReportTarget } from "../liquidaciones/OverrideEditModal";

type EnvioLegacyDetail = {
  lightdata_id: number | string;
  numero_tracking: string | null;
  id_venta_ml: string | null;
  origen: string | null;
  metodo_envio: string | null;
  bultos: string | null;
  estado: string | null;
  fecha_estado: string | null;
  fecha_estado_ar: string | null;
  estado_ml: string | null;
  subestado_ml: string | null;
  nombre_fantasia: string | null;
  cadete: string | null;
  nombre_destinatario: string | null;
  direccion: string | null;
  cp: string | null;
  localidad: string | null;
  ciudad: string | null;
  tel_destinatario: string | null;
  email_destinatario: string | null;
  latitud: string | null;
  longitud: string | null;
  fecha_venta: string | null;
  fecha_colecta: string | null;
  fecha_envio: string | null;
  zonas_costos: string | null;
  costo_envio: string | null;
  observaciones: string | null;
};

type Props = {
  open: boolean;
  lightdataId: number | string | null;
  token: string | null;
  onClose: () => void;
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 120,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
};
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const shellStyle: React.CSSProperties = { position: "relative", width: "100%", maxWidth: "1040px", maxHeight: "90vh", overflow: "hidden" };
const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "18px 20px",
  borderBottom: "1px solid var(--border)",
};
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" };
const bodyStyle: React.CSSProperties = { padding: "20px", maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "18px" };
const columnsRowStyle: React.CSSProperties = { display: "flex", gap: "20px", flexWrap: "wrap", alignItems: "flex-start" };
const leftColStyle: React.CSSProperties = { flex: "3 1 460px", display: "flex", flexDirection: "column", gap: "18px", minWidth: 0 };
const rightColStyle: React.CSSProperties = { flex: "1 1 280px", display: "flex", flexDirection: "column", gap: "12px", minWidth: 0 };
const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 10px 0",
  fontSize: "13px",
  fontWeight: 700,
  color: "var(--text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};
const gridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px" };
const fieldStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "4px" };
const labelStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const valueStyle: React.CSSProperties = { fontSize: "14px", color: "var(--text-primary)", wordBreak: "break-word" };
const stateStyle: React.CSSProperties = { padding: "24px 0", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const mapContainerStyle: React.CSSProperties = {
  height: "200px",
  borderRadius: "12px",
  overflow: "hidden",
  border: "1px solid var(--border)",
  background: "var(--surface-2, #0c0e12)",
};
const mapPlaceholderStyle: React.CSSProperties = { ...stateStyle, padding: 0, height: "200px", display: "flex", alignItems: "center", justifyContent: "center" };
const bottomBarStyle: React.CSSProperties = { borderTop: "1px solid var(--border)", paddingTop: "16px" };

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={fieldStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value ?? "—"}</span>
    </div>
  );
}

// Mapa chico read-only: centra en [lat,lng], un solo marcador fijo, sin
// controles de edición. Se crea una vez por apertura del modal (el modal
// entero desmonta al cerrar, así que no hereda el problema de ref-obsoleto
// que tuvo GeoResolutionPanel — acá alcanza con crear y, al desmontar,
// remove() para no dejar listeners de Leaflet colgados).
function EnvioMap({ lat, lng }: { lat: number; lng: number }) {
  const leaflet = useLeaflet();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    if (!leaflet || !containerRef.current || mapRef.current) return;
    const L = (window as any).L;
    const map = L.map(containerRef.current, {
      zoomControl: false,
      dragging: true,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      attributionControl: false,
    }).setView([lat, lng], 15);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 18 }).addTo(map);
    const icon = L.divIcon({
      className: "",
      html: `<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;background:var(--primary,#4f8cff);border:2px solid #1a1a1a;transform:rotate(-45deg);box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 18],
    });
    L.marker([lat, lng], { icon, interactive: false }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [leaflet, lat, lng]);

  return (
    <div style={mapContainerStyle}>
      {!leaflet ? <div style={mapPlaceholderStyle}>Cargando mapa…</div> : <div ref={containerRef} style={{ width: "100%", height: "100%" }} />}
    </div>
  );
}

// Perfiles que pueden crear un Reporte Operativo (mismo REPORT_VIEWERS que
// server/legacy/operationalReports/operationalReports.routes.js) — Picker y
// un admin sin perfil asignado no ven esta acción.
const REPORT_PROFILES = ["COORDINADOR", "LIQUIDACIONES", "ADMIN_TOTAL"];

type EntityLookup = {
  lightdataId: number | string;
  store: { id: number; name: string | null } | null;
  driver: { id: number; name: string | null } | null;
};

export default function EnvioLegacyDetailModal({ open, lightdataId, token, onClose }: Props) {
  const { adminProfile } = useAuth();
  const canCreateReport = adminProfile !== null && REPORT_PROFILES.includes(adminProfile);

  const [detail, setDetail] = useState<EnvioLegacyDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reportar un envío desde acá (Sprint Operativo — Parte 3: "el reporte
  // debe generarse desde donde realmente ocurre el problema"). Reutiliza
  // GET /v1/reports/lookup/:tracking (el mismo que usa la búsqueda de
  // Reportes Operativos) para resolver Store/Driver — nunca una segunda
  // versión de ese matching.
  const [entityLookup, setEntityLookup] = useState<EntityLookup | null>(null);
  const [reportTarget, setReportTarget] = useState<{ target: ReportTarget; label: string } | null>(null);

  useEffect(() => {
    if (!open || !lightdataId || !token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    setEntityLookup(null);

    authFetch(`/v1/envios-legacy/${lightdataId}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el detalle del envío");
        if (!cancelled) setDetail(payload.envio ?? null);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, lightdataId, token]);

  useEffect(() => {
    if (!open || !detail?.numero_tracking || !token || !canCreateReport) return;
    let cancelled = false;
    authFetch(`/v1/reports/lookup/${encodeURIComponent(detail.numero_tracking)}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && payload?.ok !== false) {
          setEntityLookup({ lightdataId: payload.envio.lightdataId, store: payload.envio.store, driver: payload.envio.driver });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, detail?.numero_tracking, token, canCreateReport]);

  if (!open) return null;

  const lat = detail?.latitud ? Number(detail.latitud) : null;
  const lng = detail?.longitud ? Number(detail.longitud) : null;
  const hasCoords = lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={onClose} />
      <div style={shellStyle}>
        <Card padding="0" style={{ display: "flex", flexDirection: "column" }}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>Envío {detail?.numero_tracking || lightdataId || ""}</h2>
            <Button variant="secondary" onClick={onClose}>
              Cerrar
            </Button>
          </div>

          <div style={bodyStyle}>
            {loading ? (
              <div style={stateStyle}>Cargando…</div>
            ) : error ? (
              <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>
            ) : !detail ? (
              <div style={stateStyle}>No se encontró el envío.</div>
            ) : (
              <>
                <div style={columnsRowStyle}>
                  <div style={leftColStyle}>
                    <section>
                      <h3 style={sectionTitleStyle}>General</h3>
                      <div style={gridStyle}>
                        <Field label="Tracking" value={detail.numero_tracking} />
                        <Field label="LightData ID" value={detail.lightdata_id} />
                        <Field label="ID venta ML" value={detail.id_venta_ml} />
                        <Field label="Store / nombre fantasía" value={detail.nombre_fantasia} />
                        <Field label="Origen" value={detail.origen} />
                        <Field label="Método de envío" value={detail.metodo_envio} />
                        <Field label="Bultos" value={detail.bultos} />
                        <Field label="Fecha venta" value={detail.fecha_venta} />
                        <Field label="Fecha colecta" value={detail.fecha_colecta} />
                        <Field label="Fecha envío" value={detail.fecha_envio} />
                      </div>
                    </section>

                    <section>
                      <h3 style={sectionTitleStyle}>Estado</h3>
                      <div style={gridStyle}>
                        <Field label="Estado operativo" value={detail.estado} />
                        <Field label="Fecha estado (Argentina)" value={detail.fecha_estado_ar} />
                        <Field label="Estado ML" value={detail.estado_ml} />
                        <Field label="Subestado ML" value={detail.subestado_ml} />
                        <Field label="Chofer" value={detail.cadete} />
                      </div>
                    </section>

                    <section>
                      <h3 style={sectionTitleStyle}>Operación</h3>
                      <div style={gridStyle}>
                        <Field label="Zona costo" value={detail.zonas_costos} />
                        <Field label="Costo envío" value={detail.costo_envio} />
                      </div>
                    </section>
                  </div>

                  <div style={rightColStyle}>
                    {hasCoords ? (
                      <EnvioMap lat={lat as number} lng={lng as number} />
                    ) : (
                      <div style={mapContainerStyle}>
                        <div style={mapPlaceholderStyle}>Sin coordenadas</div>
                      </div>
                    )}

                    <section>
                      <h3 style={sectionTitleStyle}>Destino</h3>
                      <div style={{ ...gridStyle, gridTemplateColumns: "1fr" }}>
                        <Field
                          label="Destinatario"
                          value={detail.nombre_destinatario || "Sin informar"}
                        />
                        <Field label="Dirección" value={detail.direccion} />
                        <Field label="CP" value={detail.cp} />
                        <Field label="Localidad" value={detail.localidad} />
                        <Field label="Ciudad" value={detail.ciudad} />
                        <Field label="Teléfono" value={detail.tel_destinatario} />
                        <Field label="Email" value={detail.email_destinatario} />
                        <Field label="Latitud" value={detail.latitud} />
                        <Field label="Longitud" value={detail.longitud} />
                      </div>
                      {hasCoords ? (
                        <div style={{ marginTop: "8px" }}>
                          <a
                            href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: "13px", color: "var(--primary, #4f8cff)" }}
                          >
                            Abrir en mapa ↗
                          </a>
                        </div>
                      ) : null}
                    </section>
                  </div>
                </div>

                <div style={bottomBarStyle}>
                  <h3 style={sectionTitleStyle}>Observaciones</h3>
                  <span style={valueStyle}>{detail.observaciones || "—"}</span>
                </div>

                {canCreateReport && (
                  <div style={bottomBarStyle}>
                    <h3 style={sectionTitleStyle}>Acciones</h3>
                    {!entityLookup ? (
                      <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Resolviendo Store/Driver…</span>
                    ) : !entityLookup.store && !entityLookup.driver ? (
                      <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                        Este envío no está vinculado a ningún Store ni Driver todavía — no se puede reportar.
                      </span>
                    ) : (
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        {entityLookup.store && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              setReportTarget({
                                target: { lightdataId: entityLookup.lightdataId, storeId: entityLookup.store!.id },
                                label: `Store: ${entityLookup.store!.name || "—"}`,
                              })
                            }
                          >
                            📝 Crear Reporte Operativo (Store: {entityLookup.store.name || "—"})
                          </Button>
                        )}
                        {entityLookup.driver && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              setReportTarget({
                                target: { lightdataId: entityLookup.lightdataId, driverId: entityLookup.driver!.id },
                                label: `Chofer: ${entityLookup.driver!.name || "—"}`,
                              })
                            }
                          >
                            📝 Crear Reporte Operativo (Chofer: {entityLookup.driver.name || "—"})
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      <OverrideEditModal
        open={reportTarget !== null}
        mode="report"
        entityLabel={reportTarget?.label ?? null}
        token={token}
        delivery={
          reportTarget
            ? ({
                lightdata_id: reportTarget.target.lightdataId,
                tracking: detail?.numero_tracking ?? null,
                fecha_colecta: null,
                destinatario: detail?.nombre_destinatario ?? null,
                direccion: detail?.direccion ?? null,
                original_cp: null,
                cp: detail?.cp ?? null,
                has_postal_code_override: false,
                tier: null,
                tariff_name: null,
                estado: detail?.estado ?? null,
                is_turbo: false,
                turbo_source: null,
                turbo_price: null,
                is_double: false,
                calculated_price: null,
                manual_price: null,
                final_price: null,
                pricing_status: "PENDING",
                pricing_reason: null,
                has_override: false,
                override_observation: null,
              } as DeliveryDetail)
            : null
        }
        reportTarget={reportTarget?.target}
        onClose={() => setReportTarget(null)}
        onSaved={() => setReportTarget(null)}
      />
    </div>
  );
}
